



















































import { defineComponent, PropType, computed } from "@nuxtjs/composition-api";
import { useWmAttributes } from "@wemade-vsf/catalog/lib/runtime/product/composables/useWmAttributes";
import { SfHeading, SfAccordion, SfProperty } from "@storefront-ui/vue";
import CmsContent from "components/theme/Content/CmsContent.vue";
import ProductAttachment from "components/catalog/Product/ProductAttachment.vue";
import ProductAttributeGroup from "components/catalog/Product/ProductAttributeGroup.vue";
import ProductTab from "components/catalog/Product/ProductTab.vue";
import FittingParts from "~/modules/part-finder/components/FittingParts.vue";
import type {
  Product,
  ProductAttachmentInterface,
} from "@wemade-vsf/catalog/lib/runtime/product/types";
import type { ComplexTextValue } from "@wemade-vsf/magento-api";

export default defineComponent({
  name: "ProductTabs",
  props: {
    product: {
      type: [Object, null] as PropType<Product | null>,
    },
    attachments: {
      type: [Array, null] as PropType<ProductAttachmentInterface[] | null>,
    },
  },
  components: {
    SfHeading,
    SfAccordion,
    SfProperty,
    CmsContent,
    ProductAttachment,
    ProductAttributeGroup,
    ProductTab,
    FittingParts,
    ProductLinksSlider: () =>
      import("components/catalog/Product/ProductLinksSlider.vue"),
  },
  setup(props) {
    const { attributeGroups, specificationExcludeAtts } = useWmAttributes();
    const upsellProducts = computed<string[] | null>(() =>
      props.product?.upsell_products?.length
        ? props.product.upsell_products.map((p) => p?.sku || "").filter(Boolean)
        : null
    );
    const relatedProducts = computed<string[] | null>(() =>
      props.product?.related_products?.length
        ? props.product.related_products
            .map((p) => p?.sku || "")
            .filter(Boolean)
        : null
    );
    const crosssellProducts = computed<string[] | null>(() =>
      props.product?.crosssell_products?.length
        ? props.product.crosssell_products
            .map((p) => p?.sku || "")
            .filter(Boolean)
        : null
    );
    const serviceProducts = computed<string[] | null>(() =>
      props.product?.service_products?.length
        ? props.product.service_products
            .map((p) => p?.sku || "")
            .filter(Boolean)
        : null
    );

    const productDescription = computed<ComplexTextValue>(() => {
      if (
        props.product?.configurable_product_options_selection?.variant
          ?.description
      ) {
        return props.product?.configurable_product_options_selection?.variant
          ?.description;
      }
      return props.product?.description || { html: "", styles: null };
    });

    const sku = computed<string | null>(() => {
      if (props.product?.sku) {
        return props.product?.sku;
      }
      return null;
    });

    const normalizeLabel = (label: string): string => {
      const map: Record<string, string> = {
        generelt: "Generelt",
        general: "Generelt",
      };
      return map[label.trim().toLowerCase()] || label.trim();
    };

    const filteredAttributeGroups = computed(() => {
      if (!attributeGroups.value?.length) return null;

      const mergedGroups: Record<string, any> = {};

      for (const group of attributeGroups.value) {
        const key = normalizeLabel(group.label || "");

        const attributes = (group.attributes || []).filter(
          (a) =>
            !!a.value &&
            a.is_visible_on_front &&
            !specificationExcludeAtts.includes(a.attribute_code)
        );

        if (!attributes.length) continue;

        if (!mergedGroups[key]) {
          mergedGroups[key] = {
            ...group,
            label: key.trim(),
            attributes,
          };
        } else {
          mergedGroups[key].attributes.push(...attributes);
        }
      }

      return Object.values(mergedGroups);
    });

    return {
      productDescription,
      filteredAttributeGroups,
      serviceProducts,
      crosssellProducts,
      upsellProducts,
      relatedProducts,
      sku,
    };
  },
});
